<template>
  <div>
    <div class="new-password-field spacing-top--24">
      <popper
        :key="popperKey"
        ref="popper"
        append-to-body
        trigger="clickToOpen"
        :options="{
          placement: 'left',
          modifiers: {
            adjustPositionModifier,
          },
        }">
        <div class="password-score__popover popper">
          <PasswordScore @passwordScore="setPasswordScoreCheck" :value="newPassword" />
        </div>

        <v-text-field
          slot="reference"
          :label="$t('auth.new_password')"
          class="password-input"
          :placeholder="$t('auth.password')"
          validate-on-blur
          autocomplete="new-password"
          v-model="newPassword"
          @input="$emit('input', newPassword)"
          :append-icon="hideNewPassword ? icons.mdiEyeOffOutline : icons.mdiEyeOutline"
          :rules="[rules.required, passwordStrongEnough, passwordConfirmation]"
          ref="password"
          :type="hideNewPassword ? 'password' : 'text'"
          persistent-placeholder
          hide-details="auto"
          @click:append="() => (hideNewPassword = !hideNewPassword)"
          @blur="onBlur($refs.password)"
          required>
          <template v-slot:message="{ message }">
            <v-icon size="16" color="error">{{ icons.mdiAlertCircle }}</v-icon>
            {{ message }}
          </template>
        </v-text-field>
      </popper>
    </div>
    <div class="repeat-password-field spacing-top--24">
      <v-text-field
        name="passwordRepeat"
        :label="$t('auth.repeat_password')"
        class="password-input"
        autocomplete="new-password"
        :placeholder="$t('auth.password')"
        :rules="[rules.required, addPasswordRepeatRule]"
        ref="newPasswordRepeat"
        validate-on-blur
        v-model.lazy="newPasswordRepeat"
        :append-icon="hidePasswordRepeat ? icons.mdiEyeOffOutline : icons.mdiEyeOutline"
        :type="hidePasswordRepeat ? 'password' : 'text'"
        persistent-placeholder
        hide-details="auto"
        @click:append="() => (hidePasswordRepeat = !hidePasswordRepeat)"
        @blur="onBlur($refs.newPasswordRepeat)"
        required>
        <template v-slot:message="{ message }">
          <v-icon size="16" color="error">{{ icons.mdiAlertCircle }}</v-icon>
          {{ message }}
        </template>
      </v-text-field>
    </div>
  </div>
</template>

<script>
  //Components
  import PasswordScore from "@/components/custom/PasswordScore.vue";
  // Icons
  import { mdiEyeOffOutline, mdiEyeOutline, mdiAlertCircle } from "@mdi/js";
  import Popper from "vue-popperjs";
  import "vue-popperjs/dist/vue-popper.css";

  export default {
    name: "PasswordAndPasswordRepeatBlock",
    components: {
      PasswordScore,
      Popper,
    },
    props: {
      password: {
        type: String,
        default: "",
      },
      cleanInputs: {
        type: Boolean,
        default: false,
      },
      fixedPopperPosition: {
        type: String,
        default: "",
      },
      onBlur: {
        type: Function,
        default: () => {},
      },
    },
    data() {
      return {
        icons: {
          mdiEyeOutline: mdiEyeOutline,
          mdiEyeOffOutline: mdiEyeOffOutline,
          mdiAlertCircle: mdiAlertCircle,
        },
        hideNewPassword: true,
        hidePasswordRepeat: true,
        passwordStrongEnough: false,
        newPassword: this.password,
        newPasswordRepeat: "",
        // Filler until user inputs the field once
        passwordConfirmation: true,
        rules: {
          required: value => !!value || this.$t("generic.validation.required"),
        },
        newPasswordValid: false,
        newPasswordRepeatValid: false,
        popperKey: 0,
        adjustPositionModifier: {
          name: "adjustPosition",
          enabled: true,
          phase: "main",
          fn: modifierData => {
            if (this.fixedPopperPosition) {
              if (modifierData.instance.options.placement !== this.fixedPopperPosition) {
                modifierData.instance.options.placement = "top";

                return modifierData;
              }
            } else {
              // When screen width is smaller than 854, move password score popover to the top of the input
              if (window.innerWidth < 854 && modifierData.instance.options.placement !== "top") {
                modifierData.instance.options.placement = "top";

                return modifierData;
              }
              if (window.innerWidth >= 854 && modifierData.instance.options.placement !== "left") {
                modifierData.instance.options.placement = "left";

                return modifierData;
              }
            }
          },
        },
      };
    },
    watch: {
      passwordsValidAndEquals: {
        handler(value) {
          this.$emit("passwordInputsValid", value);
        },
      },
      cleanInputs: {
        handler(value) {
          if (value === true) {
            this.newPassword = "";
            this.newPasswordRepeat = "";
          }
        },
      },
    },
    mounted() {
      this.$watch("$refs.password.valid", newVal => {
        this.newPasswordValid = newVal;
      });
      this.$watch("$refs.newPasswordRepeat.valid", newVal => {
        this.newPasswordRepeatValid = newVal;
      });
    },
    computed: {
      addPasswordRepeatRule() {
        return this.newPassword === this.newPasswordRepeat || this.$t("generic.validation.passwords_must_match");
      },
      passwordsValidAndEquals() {
        return this.newPassword === this.newPasswordRepeat && this.newPasswordValid && this.newPasswordRepeatValid;
      },
    },
    methods: {
      setPasswordScoreCheck(value) {
        // Passes 4 conditions (all of them)
        if (value.contains.length >= 4) {
          this.passwordStrongEnough = true;
        } else {
          this.passwordStrongEnough = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/functions.scss";
  @import "@core/preset/preset/typography/index.scss";
  .password-input {
    ::v-deep {
      .v-input__slot {
        .v-input__icon {
          .v-icon--link {
            color: get-color("icon");
          }
        }
      }
    }
  }
</style>
